/**
 * App-internal workspace navigation guard.
 *
 * BrowserRouter cannot use React Router's data-router `useBlocker`, while a
 * DOCX save is asynchronous and must finish before the current editor unmounts.
 * This tiny registry lets an active editor delay the navigation actions exposed
 * by `useWorkspaceNavigate` without coupling the shared sidebar to DocAgent.
 */

export type WorkspaceNavigationGuard = () => boolean | Promise<boolean>;

const guards = new Map<symbol, WorkspaceNavigationGuard>();
let activeGuardRun: Promise<boolean> | null = null;
let latestAttempt = 0;

export function registerWorkspaceNavigationGuard(
  guard: WorkspaceNavigationGuard,
): () => void {
  const key = Symbol('workspace-navigation-guard');
  guards.set(key, guard);
  return () => {
    guards.delete(key);
  };
}

async function evaluateWorkspaceNavigationGuards(): Promise<boolean> {
  const snapshot = Array.from(guards.values());
  for (const guard of snapshot) {
    try {
      if (!(await guard())) return false;
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Run `navigate` immediately when no guard is active. Otherwise wait for the
 * current guard run. Repeated clicks share one save and only the latest
 * navigation intent is committed after it succeeds.
 *
 * The boolean only reports whether navigation ran synchronously; callers that
 * historically return a synchronous value do not need an API-breaking Promise.
 */
export function runAfterWorkspaceNavigationGuards(
  navigate: () => void,
): boolean {
  if (guards.size === 0) {
    navigate();
    return true;
  }

  latestAttempt += 1;
  const attempt = latestAttempt;
  if (!activeGuardRun) {
    activeGuardRun = evaluateWorkspaceNavigationGuards().finally(() => {
      activeGuardRun = null;
    });
  }
  void activeGuardRun.then((allowed) => {
    if (allowed && attempt === latestAttempt) navigate();
  });
  return false;
}
